import type { JsonLogicJoinOperator, JsonLogicRule } from './jsonLogic';

export const ADVANCED_FILTER_SCHEMA_VERSION = '2026-08-25.1' as const;
export const ADVANCED_FILTER_LIMITS = {
    conditionsPerGroup: 50,
    definitions: 10,
    groups: 10,
    totalConditions: 100,
} as const;

export type AdvancedFilterJoin = JsonLogicJoinOperator;

export interface AdvancedFilterCondition<TRule> {
    id: string;
    /** Boolean relationship to the preceding condition. Ignored for the first condition. */
    join: AdvancedFilterJoin;
    rule: TRule;
}

export interface AdvancedFilterGroup<TRule> {
    conditions: AdvancedFilterCondition<TRule>[];
    id: string;
    /** Boolean relationship to the preceding group. Ignored for the first group. */
    join: AdvancedFilterJoin;
}

export interface AdvancedFilterDefinition<TRule> {
    groups: AdvancedFilterGroup<TRule>[];
    schemaVersion: typeof ADVANCED_FILTER_SCHEMA_VERSION | string;
}

export interface AdvancedFilterCounts {
    conditions: number;
    groups: number;
}

const createId = (prefix: 'condition' | 'group') => {
    const randomId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return `${prefix}-${randomId}`;
};

export const createAdvancedFilterDefinition = <TRule>(): AdvancedFilterDefinition<TRule> => ({
    groups: [],
    schemaVersion: ADVANCED_FILTER_SCHEMA_VERSION,
});

export const createAdvancedFilterGroup = <TRule>(join: AdvancedFilterJoin = 'and'): AdvancedFilterGroup<TRule> => ({
    conditions: [],
    id: createId('group'),
    join,
});

export const createAdvancedFilterCondition = <TRule>(
    rule: TRule,
    join: AdvancedFilterJoin = 'and',
): AdvancedFilterCondition<TRule> => ({
    id: createId('condition'),
    join,
    rule,
});

export const getAdvancedFilterCounts = <TRule>(
    definition: AdvancedFilterDefinition<TRule> | null | undefined,
): AdvancedFilterCounts => ({
    conditions: definition?.groups.reduce((count, group) => count + group.conditions.length, 0) ?? 0,
    groups: definition?.groups.length ?? 0,
});

export const getAdvancedFiltersCounts = <TRule>(
    definitions: readonly AdvancedFilterDefinition<TRule>[] | null | undefined,
): AdvancedFilterCounts =>
    (definitions ?? []).reduce<AdvancedFilterCounts>(
        (counts, definition) => {
            const next = getAdvancedFilterCounts(definition);

            return {
                conditions: counts.conditions + next.conditions,
                groups: counts.groups + next.groups,
            };
        },
        { conditions: 0, groups: 0 },
    );

export const hasAdvancedFilters = <TRule>(definition: AdvancedFilterDefinition<TRule> | null | undefined): boolean =>
    getAdvancedFilterCounts(definition).conditions > 0;

export const isAdvancedFilterDefinitionComplete = <TRule>(
    definition: AdvancedFilterDefinition<TRule> | null | undefined,
): definition is AdvancedFilterDefinition<TRule> =>
    Boolean(definition?.groups.length && definition.groups.every((group) => group.conditions.length > 0));

/**
 * Combines a user-ordered boolean sequence using conventional AND precedence.
 * Keeping the expression flat-by-run prevents deeply nested JSONLogic trees while
 * retaining the connector shown between every condition and group in the editor.
 */
export const combineAdvancedFilterSequence = (
    entries: Array<{ join: AdvancedFilterJoin; value: JsonLogicRule | undefined }>,
): JsonLogicRule | undefined => {
    const activeEntries = entries.filter(
        (entry): entry is { join: AdvancedFilterJoin; value: JsonLogicRule } => entry.value !== undefined,
    );

    if (!activeEntries.length) {
        return undefined;
    }

    const andRuns: JsonLogicRule[][] = [[]];

    activeEntries.forEach((entry, index) => {
        if (index > 0 && entry.join === 'or') {
            andRuns.push([]);
        }

        andRuns[andRuns.length - 1]?.push(entry.value);
    });

    const runRules = andRuns
        .filter((run) => run.length)
        .map<JsonLogicRule>((run) => (run.length === 1 ? run[0] : { and: run }));

    return runRules.length === 1 ? runRules[0] : { or: runRules };
};

export const buildAdvancedFilterJsonLogic = <TRule>(
    definition: AdvancedFilterDefinition<TRule> | null | undefined,
    buildRule: (rule: TRule) => JsonLogicRule | undefined,
): JsonLogicRule | undefined =>
    combineAdvancedFilterSequence(
        (definition?.groups ?? []).map((group) => ({
            join: group.join,
            value: combineAdvancedFilterSequence(
                group.conditions.map((condition) => ({
                    join: condition.join,
                    value: buildRule(condition.rule),
                })),
            ),
        })),
    );

/** Separate saved advanced filters are combined with AND while retaining each definition's internal grouping. */
export const buildAdvancedFiltersJsonLogic = <TRule>(
    definitions: readonly AdvancedFilterDefinition<TRule>[] | null | undefined,
    buildRule: (rule: TRule) => JsonLogicRule | undefined,
): JsonLogicRule | undefined => {
    const expressions = (definitions ?? [])
        .map((definition) => buildAdvancedFilterJsonLogic(definition, buildRule))
        .filter((expression): expression is JsonLogicRule => expression !== undefined);

    if (!expressions.length) {
        return undefined;
    }

    return expressions.length === 1 ? expressions[0] : { and: expressions };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isJoin = (value: unknown): value is AdvancedFilterJoin => value === 'and' || value === 'or';

export const parseAdvancedFilterDefinition = <TRule>(
    value: unknown,
    isRule: (rule: unknown) => rule is TRule,
): AdvancedFilterDefinition<TRule> | undefined => {
    if (!isRecord(value) || !Array.isArray(value.groups)) {
        return undefined;
    }

    let totalConditions = 0;
    const groups: AdvancedFilterGroup<TRule>[] = [];
    const groupIds = new Set<string>();
    const conditionIds = new Set<string>();

    for (const groupValue of value.groups.slice(0, ADVANCED_FILTER_LIMITS.groups)) {
        if (!isRecord(groupValue) || typeof groupValue.id !== 'string' || !Array.isArray(groupValue.conditions)) {
            continue;
        }
        const groupId = groupValue.id.slice(0, 160);
        if (!groupId || groupIds.has(groupId)) {
            continue;
        }
        groupIds.add(groupId);

        const conditions: AdvancedFilterCondition<TRule>[] = [];

        for (const conditionValue of groupValue.conditions.slice(0, ADVANCED_FILTER_LIMITS.conditionsPerGroup)) {
            if (
                totalConditions >= ADVANCED_FILTER_LIMITS.totalConditions ||
                !isRecord(conditionValue) ||
                typeof conditionValue.id !== 'string' ||
                !isRule(conditionValue.rule)
            ) {
                continue;
            }
            const conditionId = conditionValue.id.slice(0, 160);
            if (!conditionId || conditionIds.has(conditionId)) {
                continue;
            }
            conditionIds.add(conditionId);

            conditions.push({
                id: conditionId,
                join: isJoin(conditionValue.join) ? conditionValue.join : 'and',
                rule: conditionValue.rule,
            });
            totalConditions += 1;
        }

        groups.push({
            conditions,
            id: groupId,
            join: isJoin(groupValue.join) ? groupValue.join : 'and',
        });
    }

    return {
        groups,
        schemaVersion:
            typeof value.schemaVersion === 'string' ? value.schemaVersion.slice(0, 80) : ADVANCED_FILTER_SCHEMA_VERSION,
    };
};

export const parseAdvancedFilterDefinitions = <TRule>(
    value: unknown,
    isRule: (rule: unknown) => rule is TRule,
): AdvancedFilterDefinition<TRule>[] =>
    (Array.isArray(value) ? value : [])
        .slice(0, ADVANCED_FILTER_LIMITS.definitions)
        .map((definition) => parseAdvancedFilterDefinition(definition, isRule))
        .filter((definition): definition is AdvancedFilterDefinition<TRule> => Boolean(definition));
